import Reflux from "reflux";
import Immutable from "immutable";
import _ from "lodash";

import { getSiteState, updateSite } from "../hooks/query/useSite";
import SiteDesignStore from "./SiteDesignStore";
import { getUserId, isActive, isAdmin } from "../hooks/query/useAccount";
import AppConstants from "../constants/AppConstants";
import UIActions from "../actions/UIActions";
import SiteSettingsActions from "../actions/SiteSettingsActions";
import SiteActions from "../actions/SiteActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import UserService from "../service/UserService";
import SiteService from "../service/SiteService.mjs";
import DesignUpdate from "../vo/DesignUpdate";
import APIError from "../service/APIError";
import ErrorUtils from "../utils/ErrorUtils";
import ErrorCodes from "../constants/ErrorCodes";
import { invalidateUserBlogDomains } from "../hooks/query/useUserBlogDomains";
import { invalidateUserSites } from "../hooks/query/useSites";
import {
  addDomainToCache,
  applySiteDomainAssignment,
  getPrimaryDomain,
  getUserDomainsData,
} from "../hooks/query/useUserDomains";
import { USER_DOMAINS } from "../constants/QueryKeys";
import { subscribeToQuery } from "../utils/queryCacheBridge";

/**
 * This is store is used by the SitesPanel (Site Settings)
 */

export const TAB_BLOG = "block";
export const TAB_SITE = "site";
export const TAB_THIRD_PARTY = "third";
export const TAB_SOCIAL = "social";
export const TAB_INTEGRATIONS = "integrations";
export const TAB_UPGRADE = "upgrade";
export const SITE_SETTINGS_TABS = [
  TAB_SITE,
  TAB_BLOG,
  TAB_SOCIAL,
  TAB_INTEGRATIONS,
  TAB_UPGRADE,
];

export const TAB_LABEL_MAP = [
  { id: TAB_SITE, label: "Site Information" },
  { id: TAB_BLOG, label: "Blog" },
  { id: TAB_SOCIAL, label: "Social" },
  { id: TAB_INTEGRATIONS, label: "Integrations" },
  { id: TAB_UPGRADE, label: "Upgrade" },
];

// VIEW_TABS is the default/main view
export const VIEW_TABS = "tabs";
export const VIEW_VANITY = "vanity";
export const VIEW_DOMAIN = "domain";
export const VIEW_BLOG = "blog";
export const VIEW_GOOGLE_ANALYTICS = "google-analytics";
export const VIEW_GOOGLE_TAG_MANAGER = "google-tag-manager";
export const VIEW_META_PIXEL = "meta-pixel";
export const VIEW_BDOW = "bdow";
export const VIEW_FACEBOOK_APP_ID = "facebook-app-id";
export const VIEW_ADD_SUBDOMAIN = "add-subdomain";
export const VIEW_PUBLISH = "publish";

export const TAB_VIEW_LABEL_MAP = {
  [TAB_INTEGRATIONS]: [
    { id: VIEW_GOOGLE_ANALYTICS, label: "Google Analytics" },
    { id: VIEW_GOOGLE_TAG_MANAGER, label: "Google Tag Manager" },
    { id: VIEW_META_PIXEL, label: "Meta Pixel" },
    { id: VIEW_BDOW, label: "BDOW" },
    { id: VIEW_FACEBOOK_APP_ID, label: "Facebook App ID" },
  ],
  [TAB_SITE]: [
    { id: VIEW_DOMAIN, label: "Custom Domain" },
    { id: VIEW_VANITY, label: "Vanity URL" },
  ],
};

export const SOCIAL_LINKS_MAP = {
  slFacebook: "Facebook",
  slInstagram: "Instagram",
  slLinkedIn: "LinkedIn",
  slPinterest: "Pinterest",
  slSnapchat: "Snapchat",
  slTikTok: "TikTok",
  slTwitter: "X (Twitter)",
  slYoutube: "YouTube",
};

export const FormInputMap = {
  "site.name": "name",
  "site.subdomain": "subdomain",
};

const SiteSettingsStore = Reflux.createStore({
  storeName: 'SiteSettingsStore',
  listenables: [SiteSettingsActions],

  init() {
    this.state = {};

    this.initialSite = null;
    this.initialConfig = null;

    this.domainStoreListener = null;
    this.domainBlogStoreListener = null;

    // Listen to specific SiteActions
    this.listenTo(SiteActions.loadSite.complete, this.reset);
    this.listenTo(SiteActions.designLocked, this._onDesignLocked);
  },

  _onDesignLocked() {
    if (this.state.get("tab") === TAB_UPGRADE) {
      this.state = this.state.set("tab", TAB_SITE);
      this.trigger(this.state);
    }
  },

  _initializeState() {
    const site = getSiteState();
    const design = SiteDesignStore.getDesign();
    const { config } = design;

    this.initialSite = site;
    this.initialConfig = config;

    return Immutable.fromJS({
      saving: false,
      error: false,
      dirty: false,
      tab: TAB_SITE,
      view: VIEW_TABS,
      addSubdomainActive: false,
      addSubdomainForm: {
        saving: false,
        error: false,
        primaryDomain: null,
      },
      isSubscribed: isActive(),
      allowShare: isActive() || isAdmin(),
      site,
      config,
    });
  },

  reset() {
    this.state = this._initializeState();
    this.trigger(this.state);
  },

  onToggleSubdomainAdd(data) {
    var active = this.state.get("addSubdomainActive");

    if (_.has(data, "open")) {
      active = data.open;
    } else {
      active = !active;
    }
    if (active) {
      //get the primary domain to use in the form
      var primaryDomain = getPrimaryDomain(getUserDomainsData()?.domains);
      this.state = this.state.setIn(["addSubdomainForm", "saving"], false);
      this.state = this.state.setIn(["addSubdomainForm", "error"], false);
      this.state = this.state.setIn(
        ["addSubdomainForm", "primaryDomain"],
        Immutable.fromJS(primaryDomain)
      );
      this.onChangeView(VIEW_ADD_SUBDOMAIN);
    } else {
      this.onChangeView(VIEW_DOMAIN);
    }

    this.trigger(this.state);
  },

  onAddSubdomain(data) {
    if (_.has(data, "web_name") && !_.isEmpty(data.web_name) && _.has(data, "parent_site_id")) {
      this.state = this.state.setIn(["addSubdomainForm", "saving"], true);
      this.state = this.state.setIn(["addSubdomainForm", "error"], false);

      this.trigger(this.state);

      var subdomainInfo = _.pick(data, ["web_name", "parent_site_id"]);

      var parentDomainId = data.parent_site_id;

      UserService.addSubdomain(getUserId(), parentDomainId, subdomainInfo)
        .then(this._onAddSubdomainComplete.bind(this))
        .catch(this._onAddSubdomainFailed.bind(this));
    }
  },

  _onAddSubdomainComplete(data) {
    var newSubdomain = data.domain;

    addDomainToCache(newSubdomain);

    //reset addSubdomain form and hide it
    this.state = this.state.setIn(["addSubdomainForm", "saving"], false);
    this.state = this.state.setIn(["addSubdomainForm", "error"], false);
    this.state = this.state.set("addSubdomainActive", false);
    this.state = this.state.set("view", VIEW_DOMAIN);

    //preselect the new subdomain
    this.state = this.state.setIn(["site", "site_domain_id"], newSubdomain.site_id);

    this._checkDirty();

    this.trigger(this.state);
  },

  _onAddSubdomainFailed(err) {
    if (APIError.handle(err)) {
      return;
    }

    var error = ErrorUtils.getFormattedError(err);

    if (ErrorCodes.DOMAIN_IN_USE == error.code) {
      error.message = "That name is already in use";
    }

    this.state = this.state.setIn(["addSubdomainForm", "saving"], false);
    this.state = this.state.setIn(["addSubdomainForm", "error"], Immutable.fromJS(error));

    this.trigger(this.state);
  },

  onChangeTab(tab) {
    this.state = this.state.set("tab", tab);
    if (tab === TAB_INTEGRATIONS) {
      this.state = this.state.set("view", VIEW_GOOGLE_ANALYTICS);
    } else if (tab === TAB_SITE) {
      this.state = this.state.set("view", VIEW_TABS);
    }
    this._resetOnViewChange();

    this.trigger(this.state);
  },

  onChangeView(view, parentTab) {
    if (VIEW_DOMAIN == view) {
      this.state = this.state.set("domainList", this._toDomainListShape(getUserDomainsData()));

      if (this.domainStoreListener == null) {
        this.domainStoreListener = subscribeToQuery([USER_DOMAINS], this._domainStoreUpdated.bind(this));
      }
    }

    this.state = this.state.set("view", view);
    if (parentTab) {
      this.state = this.state.set("parentTab", parentTab);
    }
    this._resetOnViewChange();

    this.trigger(this.state);
  },

  _resetOnViewChange() {
    this.state = this.state.set("config", this.initialConfig);
    this.state = this.state.set("site", this.initialSite);
    this.state = this.state.set("error", false);
    this.state = this.state.set("dirty", false);
  },

  /**
   * Reshapes the USER_DOMAINS cache payload (`{ domains }`) into the
   * `{ domains, loaded }` shape DomainView.jsx expects from `domainList` -- the
   * contract UserDomainsStore.getState() used to provide.
   */
  _toDomainListShape(domainsData) {
    return {
      domains: domainsData?.domains ?? [],
      loaded: domainsData !== undefined,
    };
  },

  _domainStoreUpdated(domainsData) {
    this.state = this.state.set("domainList", this._toDomainListShape(domainsData));
    this.trigger(this.state);
  },

  _blogDomainStoreUpdated(domainList) {
    this.state = this.state.set("blogDomainList", domainList);
    this.trigger(this.state);
  },

  onCloseDialog() {
    if (this.domainStoreListener != null) {
      this.domainStoreListener();
      this.domainStoreListener = null;
    }

    if (this.domainBlogStoreListener != null) {
      this.domainBlogStoreListener.stop();
      this.domainBlogStoreListener = null;
    }

    UIActions.modalClose(AppConstants.MODAL_SITE_SETTINGS);
  },

  onOpenDialog() {
    UIActions.modalOpen(AppConstants.MODAL_SITE_SETTINGS, {
      onHide: SiteSettingsActions.closeDialog,
    });
  },

  onOpenDialogWithSection(section) {
    // Validate that the section is a valid tab
    const validTabs = [TAB_SITE, TAB_BLOG, TAB_SOCIAL, TAB_INTEGRATIONS, TAB_UPGRADE];
    const selectedTab = validTabs.includes(section) ? section : TAB_SITE;

    UIActions.modalOpen(AppConstants.MODAL_SITE_SETTINGS, {
      onHide: SiteSettingsActions.closeDialog,
      autoOpenSection: selectedTab,
    });

    this.state = this.state.set("tab", selectedTab);
    this.trigger(this.state);
  },

  onUpdateProperty(propInfo) {
    var path = propInfo.name.split(".");

    var value = propInfo.value;
    if (_.isObject(value)) {
      value = Immutable.fromJS(value);
    }

    if (propInfo.action) {
      switch (propInfo.action) {
        case AppConstants.UPDATE_ACTION_SET:
          this.state = this.state.setIn(path, value);
          break;
      }
    } else {
      if (!value) {
        this.state = this.state.deleteIn(path);
      } else {
        this.state = this.state.setIn(path, value);
      }
    }
    this._resetError(propInfo.name);
    this._checkDirty();

    this.trigger(this.state);
  },

  _checkDirty() {
    var config = this.state.get("config");
    var site = this.state.get("site");

    if (!Immutable.is(config, this.initialConfig) || !Immutable.is(site, this.initialSite)) {
      this.state = this.state.set("dirty", true);
    } else {
      this.state = this.state.set("dirty", false);
    }
  },

  _resetError(propName) {
    var error = this.state.get("error", false);

    if (error) {
      if (error.get("field") == FormInputMap[propName]) {
        this.state = this.state.set("error", false);
      }
    }
  },

  onSaveSettings() {
    var config = this.state.get("config");
    var site = this.state.get("site");

    var configUpdated = !Immutable.is(config, this.initialConfig);
    var siteUpdated = !Immutable.is(site, this.initialSite);

    if (configUpdated) {
      var update = new DesignUpdate();
      update.add("config", config, AppConstants.UPDATE_ACTION_SET);
      DocumentStore.dispatch({ type: COMMANDS.UPDATE_DESIGN, payload: update });
      setTimeout(() => {
        this._updateConfigComplete();
      }, 1000);
    }

    if ((configUpdated && !siteUpdated) || (!configUpdated && !siteUpdated)) {
      UIActions.modalClose(AppConstants.MODAL_SITE_SETTINGS);
    }

    if (siteUpdated) {
      this.state = this.state.set("saving", true);
      const siteObj = site.toJS(); // convert from immutable
      SiteService.updateSite(siteObj)
        .then(this.onSiteUpdateComplete.bind(this))
        .catch(this.onSiteUpdateFail.bind(this));
    }

    this.trigger(this.state);
  },

  _updateConfigComplete() {
    const design = SiteDesignStore.getDesign();

    this.initialSite = getSiteState();
    this.initialConfig = design.config;

    this.state = this.state.set("dirty", false);
    this.state = this.state.set("saving", false);
    this.state = this.state.set("view", VIEW_TABS);
    this.state = this.state.set("tab", TAB_SITE);
    this.state = this.state.set("site", this.initialSite);
    this.state = this.state.set("config", this.initialConfig);

    this.trigger(this.state);
  },

  onSiteUpdateComplete(data) {
    var site = data.site;

    updateSite(site);
    invalidateUserSites();

    applySiteDomainAssignment(site);
    invalidateUserBlogDomains();

    var update = new DesignUpdate();
    update.add("name", site.name);
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_DESIGN, payload: update });

    var currentView = this.state.get("view");
    this.state = this.state.set("saving", false);
    this.state = this.state.set("dirty", false);

    //reset initial data to the new valid values
    var design = SiteDesignStore.getDesign();

    this.initialSite = getSiteState();
    this.initialConfig = design.config;

    this.state = this.state.set("site", this.initialSite);
    this.state = this.state.set("config", this.initialConfig);

    if (VIEW_TABS == currentView) {
      UIActions.modalClose(AppConstants.MODAL_SITE_SETTINGS);
    } else {
      this.state = this.state.set("view", VIEW_TABS);
    }

    this.trigger(this.state);
  },

  onSiteUpdateFail(err) {
    if (APIError.handle(err)) {
      return;
    }

    var error = ErrorUtils.getFormattedError(err);

    this.state = this.state.set("error", Immutable.fromJS(error));
    this.state = this.state.set("saving", false);
    this.trigger(this.state);
  },

  getInitialState() {
    this.state = this._initializeState();
    return this.state;
  },

});

export default SiteSettingsStore;
